import { useEffect, useState, useCallback, useRef } from 'react';
import { encryptCheckoutParams } from '../../utils/checkout-crypto';
import { getAgencyConfigDetails } from '../../utils/user-details';
import {
  setActivePaddleCallbacks,
  clearActivePaddleCallbacks,
  PaddleCallbackRefs,
} from '../../utils/paddle-event-dispatcher';

interface UsePaddleIntegrationProps {
  onCheckoutComplete?: (data: any) => void;
  onCheckoutError?: (error: any) => void;
  onError?: (error: any) => void;
  onCheckoutPaymentError?: (error: any) => void;
  onCheckoutClosed?: () => void;
}

const PADDLE_POLL_INTERVAL = 200;

const usePaddleIntegration = ({
  onCheckoutComplete,
  onCheckoutError,
  onCheckoutPaymentError,
  onError,
  onCheckoutClosed,
}: UsePaddleIntegrationProps = {}) => {
  const [isReady, setIsReady] = useState(!!window.Paddle);

  const onCheckoutCompleteRef = useRef(onCheckoutComplete);
  const onCheckoutErrorRef = useRef(onCheckoutError);
  const onCheckoutPaymentErrorRef = useRef(onCheckoutPaymentError);
  const onErrorRef = useRef(onError);
  const onCheckoutClosedRef = useRef(onCheckoutClosed);

  const preOpenedWindowRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const callbackRefsRef = useRef<PaddleCallbackRefs>({
    onCheckoutComplete: onCheckoutCompleteRef,
    onCheckoutError: onCheckoutErrorRef,
    onCheckoutPaymentError: onCheckoutPaymentErrorRef,
    onCheckoutClosed: onCheckoutClosedRef,
  });

  useEffect(() => {
    onCheckoutCompleteRef.current = onCheckoutComplete;
    onCheckoutErrorRef.current = onCheckoutError;
    onCheckoutPaymentErrorRef.current = onCheckoutPaymentError;
    onErrorRef.current = onError;
    onCheckoutClosedRef.current = onCheckoutClosed;
  }, [
    onCheckoutComplete,
    onCheckoutError,
    onCheckoutPaymentError,
    onError,
    onCheckoutClosed,
  ]);

  // Poll until Paddle is initialized by the main init (initialize-third-party-integrations)
  useEffect(() => {
    if (window.Paddle) {
      setIsReady(true);
      return;
    }

    const interval = setInterval(() => {
      if (window.Paddle) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, PADDLE_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount: close overlay, close white-label window, clear callbacks
  useEffect(() => () => {
      // Force-close Paddle overlay if open
      try {
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }
      } catch {
        // Ignore errors when closing checkout
      }

      // Close white-label checkout window if open
      if (preOpenedWindowRef.current && !preOpenedWindowRef.current.closed) {
        preOpenedWindowRef.current.close();
        preOpenedWindowRef.current = null;
      }

      // Remove white-label message handler
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
        messageHandlerRef.current = null;
      }

      clearActivePaddleCallbacks(callbackRefsRef.current);
    }, []);

  const prepareCheckoutWindow = useCallback(() => {
    if (preOpenedWindowRef.current && !preOpenedWindowRef.current.closed) {
      return preOpenedWindowRef.current;
    }
    preOpenedWindowRef.current = window.open('', '_blank');
    return preOpenedWindowRef.current;
  }, []);

  const isWhiteLabel = !!getAgencyConfigDetails()?.clientBaseUrl;

  const openCheckout = useCallback(
    (options: any) => {
      if (isWhiteLabel) {
        const encrypted = encryptCheckoutParams(options);
        const checkoutUrl = `${process.env.REACT_APP_BASE_URL}/checkout?data=${encrypted}`;

        let targetWindow = preOpenedWindowRef.current;
        if (!targetWindow || targetWindow.closed) {
          targetWindow = window.open(checkoutUrl, '_blank');
          preOpenedWindowRef.current = targetWindow;
        } else {
          targetWindow.location.href = checkoutUrl;
          targetWindow.focus();
        }

        const handleMessage = (event: MessageEvent) => {
          if (
            event.data?.source !== 'sh-paddle-checkout' ||
            !event.data?.type
          ) {
            return;
          }

          const { type, data } = event.data;
          switch (type) {
            case 'checkout.completed':
              onCheckoutCompleteRef.current?.(data);
              window.removeEventListener('message', handleMessage);
              break;
            case 'checkout.error':
              // Let Paddle handle error display in its checkout UI.
              break;
            case 'checkout.payment.error':
              onCheckoutPaymentErrorRef.current?.(data);
              window.removeEventListener('message', handleMessage);
              break;
            case 'checkout.closed':
              onCheckoutClosedRef.current?.();
              window.removeEventListener('message', handleMessage);
              break;
            default:
              break;
          }
        };

        // Clean up any previous message handler
        if (messageHandlerRef.current) {
          window.removeEventListener('message', messageHandlerRef.current);
        }
        messageHandlerRef.current = handleMessage;
        window.addEventListener('message', handleMessage);

        return true;
      }

      if (!isReady || !window.Paddle) {
        const error = new Error('Paddle is not initialized');
        onErrorRef.current?.(error);
        return false;
      }

      try {
        // Register this component's callbacks as active before opening checkout
        setActivePaddleCallbacks(callbackRefsRef.current);
        window.Paddle.Checkout.open(options);
        return true;
      } catch (error) {
        onErrorRef.current?.(error);
        return false;
      }
    },
    [isReady, isWhiteLabel],
  );

  return {
    isLoaded: isReady,
    isInitialized: isReady,
    error: null,
    isReady,
    openCheckout,
    prepareCheckoutWindow,
  };
};

export default usePaddleIntegration;
